<template>

    <div class="container">

        <div id="messages"></div>

        <div class="page-header clearfix pt-20">
            <span class="h1">
                <i @click="showPrinterDebug=!showPrinterDebug" class="fas fa-print"></i>
                &nbsp;
                Printers
            </span>
        </div>

        <div class="qapp-widgets">
            <div class="qapp-widget-row">
                <div class="qapp-widget qw-small" :class="{'qw-green': dymoEnv.isBrowserSupported, 'qw-red': !dymoEnv.isBrowserSupported}">
                    <i class="fas" :class="{'fa-check': dymoEnv.isBrowserSupported, 'fa-times': !dymoEnv.isBrowserSupported}"></i>
                    Browser Supported
                </div>
                <div class="qapp-widget qw-small" :class="{'qw-green': dymoEnv.isFrameworkInstalled, 'qw-red': !dymoEnv.isFrameworkInstalled}">
                    <i class="fas" :class="{'fa-check': dymoEnv.isFrameworkInstalled, 'fa-times': !dymoEnv.isFrameworkInstalled}"></i>
                    Framework Installed
                </div>
                <div class="qapp-widget qw-small" :class="{'qw-green': dymoEnv.isWebServicePresent, 'qw-red': !dymoEnv.isWebServicePresent}">
                    <i class="fas" :class="{'fa-check': dymoEnv.isWebServicePresent, 'fa-times': !dymoEnv.isWebServicePresent}"></i>
                    Web Service Running
                </div>
            </div>
            <div class="qapp-widget-row">
                <div class="qapp-widget">
                    <b>{{ numLocalPrinters }}</b>
                    Local {{ numLocalPrinters === 1 ? 'Printer' : 'Printers' }}
                </div>
                <div class="qapp-widget" :class="{'qw-red': numPrinters === 0}">
                    <b>{{ numPrinters }}</b>
                    Total {{ numPrinters === 1 ? 'Printer' : 'Printers' }}
                </div>
            </div>
            <div class="qapp-widget-row" v-if="showPrinterDebug">
                <div class="qapp-widget">
                    <button @click="loadPrinters">loadPrinters</button>
                </div>
                <div class="qapp-widget">
                    <button @click="updatePrinters">updatePrinters</button>
                </div>
                <div class="qapp-widget">
                    <button @click="dymoCheckEnv">dymoCheckEnv</button>
                </div>
            </div>
        </div>

        <div class="printer-errors" v-if="showPrinterDebug">
            <p v-if="dymoEnv && dymoEnv.errorDetails && dymoEnv.errorDetails.length">{{ dymoEnv.errorDetails }}</p>
            <p v-if="numPrinters === 0">No printers found on network</p>
            <p v-for="error in errors">{{ error }}</p>
        </div>

        <div class="row">
            <div class="col-md-12">
                <table class="table table-hover" data-filter="false" data-input="#filterTable-input">
                    <thead>
                    <tr>
                        <th>Name</th>
                        <th>Model</th>
                        <th>Twin</th>
                        <th>Local</th>
                        <th>Connected</th>
                        <th>TTL</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="(printer, key) in printers">
                        <td>{{ printer.name }}</td>
                        <td>{{ printer.modelName }}</td>
                        <td>{{ printer.isTwinTurbo ? 'Yes' : 'No' }}</td>
                        <td>{{ localPrinterKeys.includes(key) ? 'Yes' : 'No' }}</td>
                        <td>{{ printer.isConnected ? 'Yes' : 'No' }}</td>
                        <td>{{ printer.ttl }}</td>
                    </tr>
                    </tbody>
                </table>
            </div>
        </div>

        <div v-if="showPrinterDebug">
            <h3>New Printer Setup (Windows)</h3>
            <ol>
                <li>Install <a href="https://www.dymo.com/online-support.html">Dymo drivers</a></li>

                <li>Give Dymo printer a unique name like "Woodwork Dept", "Pre Production" etc. It will show in the QAPP with this name.
                    <ol type="a">
                        <li>In Start Menu, search for <i><b>Printers & Scanners</b></i> and open</li>
                        <li>Click the Dymo printer, and click <i><b>Manage</b></i></li>
                        <li>Click <i><b>printer properties</b></i> on the left side</li>
                        <li>Change name from the default to your unique one, and click <i><b>OK</b></i></li>
                    </ol>
                </li>

                <li>Add Dymo Web Service to run on PC startup:
                    <ol type="a">
                        <li>Open TaskManager</li>
                        <li>Select <i><b>Startup</b></i> Tab</li>
                        <li>Right click <i><b>DYMO.WebApi.Win.Host</b></i></li>
                        <li>Click <i><b>Enable</b></i></li>
                    </ol>
                </li>

                <li>Always show Dymo Web Service icon in task bar (bottom right):
                    <ol type="a">
                        <li>In Start Menu, search for <i><b>Select which icons appear on the taskbar</b></i> and open</li>
                        <li>Enable <i><b>DYMO.WebApi.Win.Host</b></i></li>
                    </ol>
                </li>

                <li>Pin Dymo Web Service to main task bar (for easy re-launching):
                    <ol type="a">
                        <li>In Start Menu, search for <i><b>Dymo Connect Web Service</b></i></li>
                        <li>Click <i><b>Pin to taskbar</b></i></li>
                    </ol>
                </li>

                <li>Stop PC from sleeping:
                    <ol type="a">
                        <li>In Start Menu, search for <i><b>Power & Sleep Settings</b></i> and open</li>
                        <li>Under <i><b>Sleep</b></i> heading, change to <i><b>Never</b></i></li>
                    </ol>
                </li>

                <li>Keep the <a href="/printers">QAPP Printers Page</a> open in browser</li>
            </ol>

            <h3>Notes</h3>
            <ul>
                <li>Each printer needs a unique name, or one will overwrite the other. The name given to the printer in Windows will appear in the QAPP.</li>
                <li>Each PC needs to not sleep and have the QAPP printer page open, but it's fine to minimise the browser tab and do other things.</li>
                <li>Multiple printers can be plugged into one PC fine, provided they all have unique names.</li>
                <li>Dymo web connect software has a bug that shows printer is connected even after the USB is unplugged. Fix is to close the Dymo Web Connect software and reopen it. It's not enough to right click it in task bar and reset or stop&start. Needs to be exited and re started.</li>
                <li>If when printing in QAPP, nothing is coming out of printers, first check for above bug. Also watch the windows print queue before restarting the Dymo software in case 200 labels have been queued ready to print when it comes back online.</li>
                <li>The Single-Barrel Dymo printer has been set as target for all "Packing" labels (the larger ones). All other printers (Double-Barrel ones) are expected to have the small tracking labels loaded.</li>
                <li>Double-Barrel Dymos will print from one side until it's empty, then auto switch to the other. So both label sizes should be the same. Although it's fine to just load up one side.</li>
                <li>"TTL" is how long since that printer has automatically checked-in with the QAPP server. If they haven't checked in within 5 mins they're considered to be offline and will be removed from the list (that PC might crash, label printer gets hit with hammer etc)</li>
            </ul>
        </div>

    </div>

</template>

<script>

    import miniToastr from "mini-toastr";
    import api from "../../api";
    import {default as _} from "lodash";

    let Vue = window.Vue;

    export default {

        props: {
        },

        data: () => ({
            printers: {},
            loading: true,
            dymoEnv: {},
            updatePrintersInterval: null,
            loadPrintersInterval: null,
            errors: [],
            lastDymoPrinterArray: [],
            showPrinterDebug: false,
            dymoApiBaseUrl: 'https://127.0.0.1:41951/DYMO/DLS/Printing/',
        }),

        components: {
            miniToastr,
        },

        async created() {
            await this.loadPrinters();

            dymo.label.framework.init(() => {
                this.updatePrinters();
                this.updatePrintersInterval = setInterval(this.updatePrinters, 180000);
                this.loadPrintersInterval = setInterval(this.loadPrinters, 30000);
            });

        },

        mounted() {
            miniToastr.init({
                timeout: 3000,
                appendTarget: document.getElementById('messages'),
            });
        },

        destroyed() {
        },

        methods: {
            reload() {
                window.location.reload();
            },
            updatePrinters() {
                const self = this;

                dymo.label.framework.checkEnvironment(function(env) {
                    self.dymoEnv = env;
                    if (env.errorDetails.length > 0)
                        return;

                    dymo.label.framework.getPrintersAsync().then(function(dymoPrinters) {
                        self.loading = true;
                        self.lastDymoPrinterArray = dymoPrinters;
                        self.refreshEchoChannels();
                        api.updatePrinters(dymoPrinters).then(printers => {
                            self.loading = false;
                            if (api.responseHasErrors(printers)) return;
                            self.printers = printers;
                        });
                    });

                });
            },
            printLabel(type, id, printer_key, quantity) {
                let printerName = this.localPrinterKeys2Names[printer_key];
                let printParams = {};
                printParams.copies = quantity;
                printParams.jobTitle = `${quantity} x /label/${type}/${id}`;
                let printParamsXml =  dymo.label.framework.createLabelWriterPrintParamsXml(printParams);

                window.axios.get(`/label/${type}/${id}`)
                    .then((response) => {
                        dymo.label.framework.openLabelXml(response.data);
                        dymo.label.framework.printLabel(printerName, printParamsXml, response.data, '');
                    });
            },
            refreshEchoChannels() {
                let eventName = 'PrintLabel';
                let listener = (label) => {
                    console.log('Broadcast');
                    console.log(label);
                    this.printLabel(label.type, label.id, label.printer_key, label.quantity);
                };

                this.localPrinterKeys.forEach(printerKey => {
                    let channelName = 'labels.'+printerKey;
                    window.Echo.leave(channelName);
                    window.Echo.channel(channelName).listen(eventName, listener);
                });
            },
            loadPrinters() {
                this.loading = true;
                return api.loadPrinters().then(data => {
                    this.loading = false;
                    if (api.responseHasErrors(data)) return;
                    this.printers = data;
                });
            },
            dymoCheckEnv() {
                this.dymoEnv =  dymo.label.framework.checkEnvironment();
            },
        },

        computed: {
            connectedPrinters() {
                return _.filter(this.lastDymoPrinterArray, 'isConnected');
            },
            numPrinters() {
                return Object.keys(this.connectedPrinters).length;
            },
            numLocalPrinters() {
                return this.localPrinters.length;
            },
            localPrinters() {
                return _.filter(this.connectedPrinters, 'isLocal');
            },
            localPrinterKeys() {
                return Object.keys(this.localPrinterKeys2Names);
            },
            localPrinterKeys2Names() {
                let keys = {};

                this.localPrinters.forEach((printer) => {
                    let short = printer['name'].toLowerCase().replace(/[^a-z0-9]/g, '');
                    keys[short] = printer.name;
                });

                return keys;
            },
        },

    }

</script>

<style>

    .printer-errors p {
        font-style: italic;
        color: red;
        margin: 5px 20px 10px 5px;
    }
    .qapp-widgets {
        width: 100%;
        display: flex;
        flex-direction: column;
        margin-bottom: 40px;
    }
    .qapp-widget-row {
        display: flex;
        flex-direction: row;
        width: 100%;
    }
    .qapp-widget {
        border: 1px solid #515355;
        margin: 10px;
        padding: 7px 15px;
        border-radius: 4px;
        font-size: 17px;
    }
    .qapp-widget .fas,
    .qapp-widget b {
        margin-right: 4px;
    }
    .qw-green {
        color: #088208;
        border: 1px solid #088208;
    }
    .qw-red {
        color: red;
        border: 1px solid red;
    }
    .qw-small {
        font-size: 13px;
        padding: 4px 11px;
    }

    .checklist-header h1 .fas {
        margin-right: 15px;
    }
    .checklist-header h1 {
        font-size: 28px;
    }
    @media (min-width: 768px) {
        .checklist-header h1 {
            font-size: 36px;
        }
    }

    .loadingfade { opacity: 0.5; }
    .stat {
        display: inline-block;
        min-width: 28px;
        padding: 3px 5px;
        font-size: 12px;
        font-weight: 400;
        line-height: 1;
        color: #fff;
        text-align: center;
        white-space: nowrap;
        border-radius: 3px;
        margin-right: 10px;
        background-color: #6b6f6b;
        opacity: 0.75;
        flex-shrink: 0;
    }
    @media (min-width: 992px) {
        .stat {
            padding: 5px 7px;
            font-size: 14px;
            font-weight: 600;
            margin-right: 20px;
        }
    }
    .small-stat {
        background-color: #979797;
        padding: 3px 5px;
        font-size: 12px;
        font-weight: 600;
    }
    .stat-name {
        background-color: #fff;
        padding: 3px 5px;
        font-size: 11px;
        font-weight: 400;
        color: #313131;
        border: 1px solid #a8a8a8;
    }
    .stat a, .stat a:visited {
        color: #fff;
    }
    .checklist-header {
        display: flex;
        margin: 10px 0 10px 0;
        justify-content: space-between;
        align-items: center;
    }
    .checklist-subtitle {
        display: block;
        padding: 15px 0 0 25px;
        color: #666;
        margin: 0 24px;
        font-size: 15px;
    }
    @media (min-width: 768px) {
        .checklist-subtitle {
            font-size: 18px;
            padding: 15px 0 0 34px;
        }
    }
    .subtitleCurrentWeek {
        border-bottom: 2px solid #428c38;
        color: #14590b;
    }
    .checklist-week-nav {
        display: flex;
        height: 40px;
    }
    .checklist-week-nav button {
        font-size: 18px;
        color: #6c6c6c;
        margin: 0 5px;
    }
    .mini-toastr {
        position: absolute !important;
        right: 0 !important;
        top: 0 !important;
    }
    .mini-toastr-notification__message {
        padding: 0 !important;
    }
    #messages {
        position: relative;
    }
    .mini-toastr__notification.-success {
        background-color: #15831a !important;
    }


    .ps-filter-row {
        display: flex;
        align-items: center;
        flex-direction: row;
        justify-content: flex-end;
    }
    .ps-filter-col {
        flex-grow: 1;
        max-width: 20%;
        margin-left: 40px;
    }
    .ps-filter-col-btn {
        flex-grow: 0;
        max-width: 20%;
    }


    .tt-filter-active {}

    .tt-filter .vs__selected {
        white-space: nowrap;
    }
    .tt-filter .vs__selected-options {
        overflow: hidden;
        flex-wrap: nowrap;
    }
    .tt-filter .vs__state-active-bg {
        overflow: hidden;
    }


    .row-filter {
        margin-bottom: 20px;
    }
    .col-filter {
        margin-top: 15px;
        border: 2px solid #fff;
        border-radius: 5px;
    }
    @media (min-width:992px) {
        .col-filter {
            margin-top: 0;
        }
    }

    .col-filter-label {
        font-size: 18px;
        margin: 0 0 8px 0;
        color: #505050;
    }
    .col-filter-badge {
        margin: 0 0 0 10px;
        background-color: #bfbfbf;
        color: #fff;
        border-radius: 4px;
        display: inline-block;
        font-size: 12px;
        vertical-align: top;
    }

    .col-filter-active .vs__dropdown-toggle {
        border: 1px solid #9ea193;
    }
    .col-filter-active .col-filter-label {
        font-weight: bold;
        color: #3f3f3f;
    }

    .col-filter-highlight {
        border: 2px solid #808080;
    }


    .nopoint {
        pointer-events: none;
    }
    .large-status {
    }
    .large-status .vs__open-indicator,
    .large-status .vs__actions,
    .large-status .vs__search {

    }
    .large-status .vs__search {
        padding: 0;
    }
    .large-status .vs__dropdown-toggle {
        border: none;
    }
    .large-status.vs--single.vs--open .vs__selected {
        position: relative;
    }
    .large-status .vs__dropdown-option--selected,
    .large-status .vs__dropdown-option,
    .large-status .vs__dropdown-option--highlight,
    .large-status .vs__dropdown-option--selected:hover,
    .large-status .vs__dropdown-option:hover,
    .large-status .vs__dropdown-option--highlight:hover {
        background-color: #fff;
    }

    .large-status.vs--disabled .vs__clear, .large-status.vs--disabled .vs__dropdown-toggle,
    .large-status.vs--disabled .vs__open-indicator, .large-status.vs--disabled .vs__search,
    .large-status.vs--disabled .vs__selected {
        background-color: #fff;
        opacity: 0.9;
    }
    .btn.disabled, .btn[disabled], fieldset[disabled] .btn {
        filter: alpha(opacity=90) !important;
        opacity: .9 !important;
    }


    .vs__dropdown-option {
        padding: 6px 20px;
        font-size: 18px;
    }

    .vs__clear {
        fill: rgba(95, 95, 95, 0.5);
        padding: 0;
        border: 1px solid #e0e0e0;
        background-color: #e7e7e7;
        cursor: pointer;
        margin: 0 4px 0 0;
        width: 25px;
        height: 25px;
        border-radius: 20px;
    }
    @media (min-width: 1200px) {
        .vs__clear {
            margin: 0 12px 0 0;
        }
    }


    .btn-large-status {
        color: #fff;
        font-size: 35px;
        padding: 6px 16px;
        margin: 0 10px 0 0;
    }
    .btn-large-status .fa {
        color: #fff;
    }
    .btn-large-status:hover, .btn-large-status:active .btn-large-status:focus {
        color: #fff;
    }
    .large-status-badge {
        font-size: 15px;
        padding: 5px 10px;
    }
    @media (min-width: 1200px) {
        .large-status-badge {
            font-size: 18px;
        }
    }

</style>